import { useCallback, useEffect, useRef } from 'react';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS, ANALYTICS_SOURCES } from 'shared/constants/analytics';
import _ from 'lodash';
import { nanoid } from 'nanoid';

/**
 * Custom hook for handling all analytics tracking in the My Content page
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.filters - Current filters state
 * @param {Array} params.contentItems - Current content items
 * @param {number} params.totalItems - Total number of items
 * @param {number} params.expiredCount - Number of expired items
 * @param {boolean} params.pageLoading - Page loading state
 * @param {boolean} params.searchLoading - Search loading state
 * @param {Array} params.selectedRowKeys - Currently selected row keys
 * @returns {Object} Analytics functions and handlers
 */
export const useMyContentAnalytics = ({
  filters,
  contentItems,
  totalItems,
  expiredCount,
  pageLoading,
  searchLoading,
  selectedRowKeys,
}) => {
  const prevLoadingRef = useRef(false);
  const currentImpressionIdRef = useRef(null);

  /**
   * Tracks page view analytics event
   */
  const trackPageView = useCallback(() => {
    const filtersData = {
      searchFilter: filters.searchText || filters.term,
      ownerFilter: filters.ownerIds?.length > 0 ? filters.ownerIds.join(',') : undefined,
      expiredFilter: filters.expiredStatus,
      sortBy: filters.sortedBy,
      sortOrder: filters.sortedOrder,
    };

    const cleanFilters = _.omitBy(filtersData, _.isEmpty);

    analyticsService.track(EVENTS.MY_CONTENT.PAGE_VIEW, {
      source: ANALYTICS_SOURCES.MY_CONTENT,
      ...cleanFilters,
    });
  }, [filters]);

  /**
   * Tracks items impression analytics event
   */
  const trackItemsImpression = useCallback(() => {
    const isCurrentlyLoading = pageLoading || searchLoading;
    const wasLoading = prevLoadingRef.current;

    // Initialize impression ID on first render with data if not set
    if (!isCurrentlyLoading && contentItems !== null && !currentImpressionIdRef.current) {
      currentImpressionIdRef.current = nanoid();
    }

    // Update the ref for next render
    prevLoadingRef.current = isCurrentlyLoading;

    if (wasLoading && !isCurrentlyLoading && contentItems !== null) {
      const expiredPercentage = totalItems > 0 ? Math.round((expiredCount / totalItems) * 100) : 0;

      // Generate new impression ID for each impression event
      const impressionId = nanoid();
      currentImpressionIdRef.current = impressionId;

      analyticsService.track(EVENTS.MY_CONTENT.ITEMS_IMPRESSION, {
        impressionId,
        numberOfItems: totalItems,
        expiredPercentage,
        hasResults: totalItems > 0,
        currentPage: filters.page,
        sortBy: filters.sortedBy,
        sortOrder: filters.sortedOrder,
        source: ANALYTICS_SOURCES.MY_CONTENT,
        filters: {
          searchFilter: filters.searchText || filters.term || '',
          ownerFilter: filters.ownerIds?.length > 0 ? filters.ownerIds.join(',') : '',
          specialtyIds: filters.specialtyIds?.length > 0 ? filters.specialtyIds.join(',') : '',
          expiredFilter: filters.expiredStatus || '',
        },
      });
    }
  }, [contentItems, totalItems, expiredCount, pageLoading, searchLoading, filters]);

  /**
   * Generic function to track action events with consistent properties
   * @param {string} eventType - The event type from EVENTS.MY_CONTENT
   * @param {Object} actionProperties - Specific properties for this action
   * @param {boolean} isBulk - Whether this is a bulk operation
   */
  const trackActionEvent = useCallback(
    (eventType, actionProperties = {}, isBulk = false) => {
      const baseProperties = {
        source: ANALYTICS_SOURCES.MY_CONTENT,
        isBulk,
        impressionId: currentImpressionIdRef.current,
      };

      if (isBulk) {
        baseProperties.itemCount = selectedRowKeys.length;
        baseProperties.itemIds = selectedRowKeys;
      }

      analyticsService.track(eventType, {
        ...baseProperties,
        ...actionProperties,
      });
    },
    [selectedRowKeys],
  );

  /**
   * Tracks click on knowledge item
   * @param {Object} record - The item record
   */
  const handleItemClick = useCallback(record => {
    analyticsService.track(EVENTS.MY_CONTENT.CLICK_ITEM, {
      itemId: record.id,
      itemTitle: record.title,
      itemType: record.type,
      source: ANALYTICS_SOURCES.MY_CONTENT,
    });
  }, []);

  /**
   * Tracks click on owner
   * @param {string} ownerId - The owner ID
   */
  const handleOwnerClick = useCallback(ownerId => {
    analyticsService.track(EVENTS.MY_CONTENT.CLICK_OWNER, {
      contactId: ownerId,
      source: ANALYTICS_SOURCES.MY_CONTENT,
    });
  }, []);

  /**
   * Tracks click on path/category
   * @param {string} categoryId - The category ID
   */
  const handlePathClick = useCallback(categoryId => {
    analyticsService.track(EVENTS.MY_CONTENT.CLICK_PATH, {
      categoryId,
      source: ANALYTICS_SOURCES.MY_CONTENT,
    });
  }, []);

  // Track page view on mount only (once)
  useEffect(() => {
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track items impression when data changes or filters change
  useEffect(() => {
    trackItemsImpression();
  }, [trackItemsImpression]);

  return {
    trackPageView,
    trackItemsImpression,
    trackActionEvent,
    handleItemClick,
    handleOwnerClick,
    handlePathClick,
  };
};
